import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Statistic, Row, Col, Button, Table } from "antd";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import "./DetailView.css";
import { useSelector } from "react-redux";
import rollbackIcon from "../../assets/icons/rollback.png";
import totalEmissionIcon from "../../assets/icons/total-emission.png";
import supplierEngagemenetIcon from "../../assets/icons/supplier-engagement.png";
import rankingIcon from"../../assets/icons/quality.png"
import emissionAvoidedIcon from "../../assets/icons/emission-avoided.png";
import emissionReductionIcon from "../../assets/icons/emission-reduction.png";
import carbonReportedIcon from "../../assets/icons/carbonreported_icon.png"
import ExcelTable from "./ExcelTable";

const getStatisticsIcon = (title) => {
  let icons = null;
  if (title === "Total Emission") {
    icons = totalEmissionIcon;
  } else if (title === "Emission Avoided") {
    icons = emissionAvoidedIcon;
  } else if (title === "Percentage Reduction") {
    icons = emissionReductionIcon;
  } else if (title === "YOY %") {
    icons = carbonReportedIcon;
  }else if(title==="Supplier Rank"){
    icons=rankingIcon;
  }
  return icons;
};

const CustomLegend = ({ payload, colors, type }) => {
  const getColorBox = (type, colors, index) => {
    let colorBox = null;
    if (type === "YOY") {
      colorBox = (
        <span
          className="legend-color mx-4 my-2 w-[20px] h-[5px]"
          style={{
            backgroundColor: colors[index % colors.length],
            border: "1px solid #010101",
          }}
        />
      );
    }
    return colorBox;
  };

  return (
    <div className="custom-legend">
      <span className="text-center text-xs">Legend</span>
      {payload.map((entry, index) => (
        <div key={`item-${index}`} className="legend-item">
          {getColorBox(type, colors, index)}
          <span className="text-xs">{entry.name}</span>
        </div>
      ))}
    </div>
  );
};

const CarbonEmissionDetailView = () => {
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const role = sessionStorage.getItem("Role");
  
  const { carbonEmissionAvoided } = useSelector((state) => state.dashboard);

  const YOY_COLORS = ["#277A30", "#144FA8", "#F4783F"];

  const CARBON_EMISSION_LEGEND = [
    { name: "Energy" },
    { name: "Packaging" },
    { name: "Waste" }
  ];
  const TABLE_COLUMNS = [
    {
      title: "Company Name",
      dataIndex: "companyName",
      key: "companyName",
      align: "center",
    },
    {
      title:"Rank",
      dataIndex:"rank",
      key:"rank",
      align:"center"
    },
    {
      title: "Total Emission",
      dataIndex: "totalEmission",
      key: "totalEmission",
      align: "center",
    },
    {
      title: "Total Emission Avoided",
      dataIndex: "totalEmissionAvoided",
      key: "totalEmissionAvoided",
      align: "center",
    },
    {
      title: "Reduced Percentage",
      dataIndex: "percentageReduction",
      key: "percentageReduction",
      align: "center",
    },
    {
      title: "Yoy Percentage",
      dataIndex: "yoyPercentage",
      key: "yoyPercentage",
      align: "center",
    }]

  useEffect(()=>{
    if(!carbonEmissionAvoided)
    {
      const basePath = location.pathname.includes("/admin") ? "/admin" : "/supplier";
      navigate(`${basePath}/dashboard`);
    }
  },[carbonEmissionAvoided])

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);


  const CARBON_EMISSION_STATISTICS = carbonEmissionAvoided ? [
    {
      title: "Total Emission",
      value: `${carbonEmissionAvoided.totalEmission.toFixed(2)} MTCO2`,
    },
    {
      title: "Emission Avoided",
      value: `${carbonEmissionAvoided.totalEmissionAvoided.toFixed(2)} MTCO2`,
    },
    {
      title: "Percentage Reduction",
      value: `${carbonEmissionAvoided.percentageReduction.toFixed(2)} %`,
    },
    {
      title: "YOY %",
      value: `${carbonEmissionAvoided.percentageChanged.toFixed(2)} %`,
    },
    ...(role == "System Admin" ? [] : [{
      title: "Supplier Rank",
      value: (`${carbonEmissionAvoided.supplierRank}/${carbonEmissionAvoided.totalSuppliers}`)||0
    }])
  ] : [];


  const generateLineChartData = () => {
    if (!carbonEmissionAvoided) return [];
    
    const { emissionAvoidedByPillars, totalEmissionAvoided, lyTotalEmissionAvoided } = carbonEmissionAvoided;
    
    return [
      {
        name: "2024",
        energy: 0,
        packaging: 0,
        waste: 0
      },
      {
        name: "2025",
        energy: (emissionAvoidedByPillars?.Energy / 100) * totalEmissionAvoided || 0,
        packaging: (emissionAvoidedByPillars?.Packaging / 100) * totalEmissionAvoided || 0,
        waste: (emissionAvoidedByPillars?.Waste / 100) * totalEmissionAvoided || 0
      }
    ];
  };

  const handleBackClick = () => {
    const basePath = location.pathname.includes("/admin") ? "/admin" : "/supplier";
    navigate(`${basePath}/dashboard`);
  };
console.log(carbonEmissionAvoided,"hello")

  return (
    <div className="md:p-0 p-2">
      <div className="detail-view-header flex justify-between items-center mb-4 md:flex-row flex-col-reverse">
        <div className="heading text-2xl font-medium text-[#004481]">
          Carbon Emission Avoided
        </div>
        <Button
          shape="round"
          icon={<img src={rollbackIcon || "/placeholder.svg"} alt={"back"} className="w-10 h-5" />}
          size="60px"
          className="top-[3px] w-[100px] h-[30px] bg-[#F8FAFC] self-end"
          style={{ border: "1px solid #A1BAD1" }}
          onClick={handleBackClick}
        >
          Back
        </Button>
      </div>

      <div className="outer-card p-4 rounded-2xl shadow-md bg-[#F8FAFC] mb-6">
        <Row gutter={[16, 16]}>
          {CARBON_EMISSION_STATISTICS?.map((stat, index) => (
              <Col key={index} xs={24} sm={12} md={role == "System Admin" ? 8 : 7} lg={role == "System Admin" ? 6 : 8}>
              <div className="statistic-card p-4 rounded-2xl bg-white ml-2">
                <img 
                  src={getStatisticsIcon(stat?.title)} 
                  alt="icon" 
                  className="md:w-8 md:h-8 w-6 h-6" 
                />
                <Statistic
                  title={
                    <span className="statistic-title text-sm">
                      {stat?.title}
                    </span>
                  }
                  value={stat?.value}
                  valueStyle={{
                    color: "rgb(1 77 78)",
                    fontSize: isMobile ? "20px" : "20px",
                    fontWeight: 600,
                    textAlign: "left",
                    marginLeft: "10px",
                  }}
                />
              </div>
            </Col>
          ))}
        </Row>
      </div>

      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
        <Col xs={{ span: 24, order: 2 }} xl={{ span: 12, order: 1 }}>
          <div className="chart-card rounded-2xl shadow-md bg-white mb-6">
            <div className="flex-container flex flex-col md:flex-row">
              <div className="chart-container w-full md:w-2/3">
                <h3 className="chart-header text-sm font-medium mb-4 text-[#000000] p-4">
                  Carbon Emission Distribution
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                    <Pie
                      data={[
                        { name: "Energy", value: carbonEmissionAvoided?.emissionAvoidedByPillars?.Energy || 0 },
                        { name: "Packaging", value: carbonEmissionAvoided?.emissionAvoidedByPillars?.Packaging || 0 },
                        { name: "Waste", value: carbonEmissionAvoided?.emissionAvoidedByPillars?.Waste || 0 }
                      ]}
                      cx="60%"
                      cy="40%"
                      outerRadius="80%"
                      innerRadius="20%"
                      fill="#8884d8"
                      dataKey="value"
                      label
                    >
                      {[0, 1, 2].map((index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={YOY_COLORS[index % YOY_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `${value.toFixed(2)}%`} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="legend-container ml-0 md:ml-4 mt-4 md:mt-0 w-auto">
                <CustomLegend
                  payload={CARBON_EMISSION_LEGEND}
                  colors={YOY_COLORS}
                  type="YOY"
                />
              </div>
            </div>
          </div>
        </Col>
        <Col xs={{ span: 24, order: 2 }} xl={{ span: 12, order: 1 }}>
          <div className="chart-card rounded-2xl shadow-md bg-white mb-6">
            <div className="flex-container flex flex-col md:flex-row">
              <div className="chart-container w-full md:w-2/3">
                <h3 className="chart-header text-sm font-medium mb-4 text-[#000000] p-4">
                  Emission Avoidance Trends
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={carbonEmissionAvoided?.emissionByPillarsYoY}
                    margin={{ top: 20 }}
                    accessibilityLayer
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" padding={{ left: 30, right: 30 }} />
                    <YAxis />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="energy"
                      stroke="#277A30"
                      activeDot={{ r: 8 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="packaging"
                      stroke="#144FA8"
                    />
                    <Line
                      type="monotone"
                      dataKey="waste"
                      stroke="#F4783F"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="legend-container ml-2 md:ml-4 mt-4 md:mt-0 ">
                <CustomLegend
                  payload={CARBON_EMISSION_LEGEND}
                  colors={YOY_COLORS}
                  type="YOY"
                />
              </div>
            </div>
          </div>
        </Col>
      </Row>
      {role=="System Admin" && <div className="flex justify-end mr-2 mt-2 mb-1">
        <ExcelTable tableData={carbonEmissionAvoided?.tableData} title="Carbon Emission Avoided" />
      </div>
      }
      
      {role=="System Admin" && <Row className="mt-2 p-[8px]">
        <Col span={24}>
          <div className="bg-white rounded-lg border-2 border-[#d1d5dc]">
          <Table columns={TABLE_COLUMNS} dataSource={carbonEmissionAvoided.tableData} pagination={false} />
          </div>
        </Col>
      </Row>}
    </div>
  );
};

export default CarbonEmissionDetailView;